import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Building, MapPin, Star, Calendar, 
  Coffee, Clock, Heart, AlertTriangle, Edit3, Save, X, 
  Lock, Globe, Users, Eye, Plus, Check, Info
} from 'lucide-react';
import { ENHANCEMENT_FIELDS, ENHANCEMENT_CATEGORIES, PRIVACY_LEVELS, DATA_SOURCES } from '../utils/profileEnhancements.js';
import ProfileEnhancementStorage from '../utils/profileEnhancementStorage.js';
import { UserStorage } from '../utils/userStorage.js';
import CRMExport from '../utils/crmExport.js';
import { logger } from '../utils/logger';

const CustomerProfileView = ({ selectedCustomer, setCurrentView }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [enhancements, setEnhancements] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editPrivacy, setEditPrivacy] = useState(PRIVACY_LEVELS.PUBLIC);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');

  useEffect(() => {
    const user = UserStorage.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      loadEnhancements(user);
    }
  }, [selectedCustomer]);

  const loadEnhancements = (user) => {
    if (selectedCustomer) {
      const providerEnhancements = ProfileEnhancementStorage.getProviderEnhancements(
        selectedCustomer.id, 
        user.id, 
        user.userType
      );
      setEnhancements(providerEnhancements);
    }
  };

  const handleEditField = (fieldName, currentValue = '', currentPrivacy = PRIVACY_LEVELS.PUBLIC) => {
    setEditField(fieldName);
    setEditValue(Array.isArray(currentValue) ? currentValue.join(', ') : currentValue);
    setEditPrivacy(currentPrivacy);
    setEditMode(true);
  };

  const handleSaveField = async () => {
    if (!currentUser || !editField) return;

    try {
      let processedValue = editValue.trim();
      
      // Handle array fields
      const field = ENHANCEMENT_FIELDS[editField];
      if (field && field.type === 'array') {
        processedValue = processedValue.split(',').map(item => item.trim()).filter(item => item);
      }

      await ProfileEnhancementStorage.addEnhancementWithPermissions(
        selectedCustomer.id,
        editField,
        processedValue,
        currentUser.id,
        editPrivacy,
        null, // Profile owner ID - will be set when we implement NPI linking
        currentUser.userType
      );

      // Reload enhancements
      loadEnhancements(currentUser);
      
      // Reset edit state
      setEditMode(false);
      setEditField(null);
      setEditValue('');
    } catch (error) {
      logger.error('Error saving enhancement:', error);
      alert('Failed to save enhancement. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditField(null);
    setEditValue('');
    setNewFieldName('');
    setShowAddField(false);
  };

  const handleAddNewField = () => {
    if (newFieldName && ENHANCEMENT_FIELDS[newFieldName]) {
      const field = ENHANCEMENT_FIELDS[newFieldName];
      setEditField(newFieldName);
      setEditValue('');
      setEditPrivacy(field.defaultPrivacy);
      setShowAddField(false);
      setNewFieldName('');
    }
  };

  const handleExportToCRM = (format) => {
    if (!currentUser || !selectedCustomer) return;

    try {
      const exportData = CRMExport.exportProvider(selectedCustomer, currentUser.id, format);
      const filename = CRMExport.generateFilename(selectedCustomer, format);
      
      if (format === 'csv') {
        const csvData = CRMExport.exportToCSV([selectedCustomer], currentUser.id);
        CRMExport.downloadFile(csvData, filename, 'text/csv');
      } else {
        CRMExport.downloadFile(exportData, filename, 'application/json');
      }
      
      alert(`Profile exported successfully as ${filename}`);
    } catch (error) {
      logger.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const getPrivacyIcon = (privacyLevel) => {
    switch (privacyLevel) {
      case PRIVACY_LEVELS.PUBLIC:
        return <Globe className="w-3 h-3 text-green-600" title="Public - Visible to all users" />;
      case PRIVACY_LEVELS.PROFESSIONAL:
        return <Users className="w-3 h-3 text-blue-600" title="Professional - Visible to healthcare professionals" />;
      case PRIVACY_LEVELS.PRIVATE:
        return <Lock className="w-3 h-3 text-red-600" title="Private - Only visible to you" />;
      case PRIVACY_LEVELS.TEAM:
        return <Users className="w-3 h-3 text-purple-600" title="Team - Visible to your organization" />;
      default:
        return <Eye className="w-3 h-3 text-gray-600" />;
    }
  };

  const getDataSourceBadge = (dataSource) => {
    const source = Object.values(DATA_SOURCES).find(s => s.type === dataSource);
    if (!source) return null;
    
    return (
      <span 
        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 ml-2"
        title={source.description}
      >
        <span className="mr-1">{source.icon}</span>
        {source.label}
      </span>
    );
  };

  const renderEnhancedField = (fieldName, defaultValue, icon) => {
    const enhancement = enhancements[fieldName];
    const value = enhancement ? enhancement.current.value : defaultValue;
    const isEditing = editMode && editField === fieldName;

    if (!value && !currentUser) return null;

    return (
      <div className="flex items-start space-x-3 group">
        {icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-medium text-gray-700">
              {ENHANCEMENT_FIELDS[fieldName]?.label || fieldName}
              {enhancement && getDataSourceBadge(enhancement.current.dataSource)}
            </p>
            {currentUser && !isEditing && (
              <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {enhancement && getPrivacyIcon(enhancement.current.privacyLevel)}
                <button
                  onClick={() => handleEditField(fieldName, value, enhancement?.current.privacyLevel)}
                  className="text-blue-600 hover:text-blue-700"
                  title="Edit this field"
                >
                  <Edit3 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          
          {isEditing ? (
            <div className="mt-2 space-y-3">
              <div>
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={ENHANCEMENT_FIELDS[fieldName]?.placeholder}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-xs text-gray-600">Privacy:</label>
                  <select
                    value={editPrivacy}
                    onChange={(e) => setEditPrivacy(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    {ENHANCEMENT_FIELDS[fieldName]?.allowedPrivacy.map(level => (
                      <option key={level} value={level}>
                        {level === PRIVACY_LEVELS.PUBLIC ? 'Public' :
                         level === PRIVACY_LEVELS.PROFESSIONAL ? 'Professional' :
                         level === PRIVACY_LEVELS.PRIVATE ? 'Private' : 'Team'}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSaveField}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  >
                    <Save className="w-3 h-3" />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center space-x-1 px-3 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50"
                  >
                    <X className="w-3 h-3" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-600">
                {Array.isArray(value) ? value.join(', ') : value || 'Not specified'}
              </p>
              {enhancement && enhancement.alternatives.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {enhancement.alternatives.length} other suggestion{enhancement.alternatives.length !== 1 ? 's' : ''} available
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!selectedCustomer) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No provider selected</p>
          <button
            onClick={() => setCurrentView('find')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Find Providers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-gray-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedCustomer.name}</h2>
                <p className="text-gray-600">{selectedCustomer.specialty} at {selectedCustomer.hospital}</p>
                <div className="flex items-center mt-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="ml-1 text-sm font-semibold">{selectedCustomer.rating}</span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({selectedCustomer.meetingsCompleted} meetings completed)
                  </span>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setCurrentView('book-meeting')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Schedule Meeting
              </button>
              
              {/* Export dropdown */}
              {currentUser && (
                <div className="relative group">
                  <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg flex items-center space-x-2">
                    <span>Export to CRM</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => handleExportToCRM('salesforce')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Salesforce Format
                      </button>
                      <button
                        onClick={() => handleExportToCRM('hubspot')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        HubSpot Format
                      </button>
                      <button
                        onClick={() => handleExportToCRM('pipedrive')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Pipedrive Format
                      </button>
                      <div className="border-t border-gray-100"></div>
                      <button
                        onClick={() => handleExportToCRM('csv')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        CSV Export
                      </button>
                      <button
                        onClick={() => handleExportToCRM('generic')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Generic JSON
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => setCurrentView('find')}
                className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg"
              >
                Back to Search
              </button>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                {currentUser && !editMode && (
                  <button
                    onClick={() => setShowAddField(!showAddField)}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Info</span>
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {/* Core address from provider data */}
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-700">Address</p>
                    <p className="text-gray-600">{selectedCustomer.address}</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mt-1">
                      <span className="mr-1">🏛️</span>
                      NPI Registry
                    </span>
                  </div>
                </div>

                {/* Enhanced fields */}
                {renderEnhancedField('officePhone', selectedCustomer.phone, <Phone className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('officeEmail', selectedCustomer.email, <Mail className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('directPhone', '', <Phone className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('officeHours', '', <Clock className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('parkingInstructions', '', <MapPin className="w-5 h-5 text-gray-400 mt-1" />)}
                
                {/* Private fields only visible to contributor */}
                {currentUser && (
                  <>
                    {renderEnhancedField('personalCell', '', <Phone className="w-5 h-5 text-red-400 mt-1" />)}
                    {renderEnhancedField('personalEmail', '', <Mail className="w-5 h-5 text-red-400 mt-1" />)}
                  </>
                )}

                {/* Practice info */}
                <div className="flex items-start space-x-3">
                  <Building className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-700">Practice</p>
                    <p className="text-gray-600">{selectedCustomer.hospital}</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mt-1">
                      <span className="mr-1">🏛️</span>
                      NPI Registry
                    </span>
                  </div>
                </div>
              </div>

              {/* Add field interface */}
              {showAddField && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <select
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select field to add...</option>
                      {Object.entries(ENHANCEMENT_FIELDS)
                        .filter(([key, field]) => field.category === 'contact' && !enhancements[key])
                        .map(([key, field]) => (
                          <option key={key} value={key}>{field.label}</option>
                        ))}
                    </select>
                    <button
                      onClick={handleAddNewField}
                      disabled={!newFieldName}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setShowAddField(false)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Meeting Preferences */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Meeting Preferences</h3>
                {currentUser && !editMode && (
                  <button
                    onClick={() => setShowAddField(!showAddField)}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Preference</span>
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {/* Enhanced preference fields */}
                {renderEnhancedField('preferredMeetingTime', selectedCustomer.preferredTime, <Clock className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('preferredRestaurants', selectedCustomer.favoriteRestaurants, <Coffee className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('dietaryRestrictions', selectedCustomer.dietaryRestrictions, <Heart className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('subspecialty', '', <User className="w-5 h-5 text-gray-400 mt-1" />)}
                {renderEnhancedField('researchInterests', '', <Building className="w-5 h-5 text-gray-400 mt-1" />)}
                
                {/* Private relationship fields */}
                {currentUser && (
                  <>
                    {renderEnhancedField('communicationStyle', '', <Users className="w-5 h-5 text-purple-400 mt-1" />)}
                    {renderEnhancedField('relationshipNotes', '', <Edit3 className="w-5 h-5 text-purple-400 mt-1" />)}
                  </>
                )}

                {/* Core provider data */}
                {(selectedCustomer.source === 'NPI Registry' || selectedCustomer.source === 'CMS NPI Registry') && (
                  <div className="flex items-start space-x-3">
                    <Building className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium text-gray-700">NPI Number</p>
                      <p className="text-gray-600">{selectedCustomer.npi}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mt-1">
                        <span className="mr-1">🏛️</span>
                        NPI Registry
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Show credentials for CMS providers */}
                {selectedCustomer.source === 'CMS NPI Registry' && selectedCustomer.credential && (
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium text-gray-700">Credentials</p>
                      <p className="text-gray-600">{selectedCustomer.credential}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mt-1">
                        <span className="mr-1">🏛️</span>
                        NPI Registry
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Show fax for CMS providers */}
                {selectedCustomer.source === 'CMS NPI Registry' && selectedCustomer.fax && (
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium text-gray-700">Fax</p>
                      <p className="text-gray-600">{selectedCustomer.fax}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mt-1">
                        <span className="mr-1">🏛️</span>
                        NPI Registry
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Add field interface for preferences */}
              {showAddField && !editMode && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <select
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select field to add...</option>
                      {Object.entries(ENHANCEMENT_FIELDS)
                        .filter(([key, field]) => 
                          (field.category === 'preferences' || field.category === 'professional' || field.category === 'relationship') && 
                          !enhancements[key]
                        )
                        .map(([key, field]) => (
                          <option key={key} value={key}>{field.label}</option>
                        ))}
                    </select>
                    <button
                      onClick={handleAddNewField}
                      disabled={!newFieldName}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setShowAddField(false)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Privacy Legend */}
          {currentUser && (
            <div className="mt-8 pt-8 border-t">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Privacy & Data Sources
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="font-medium text-gray-700 mb-2">Privacy Levels:</p>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Globe className="w-3 h-3 text-green-600" />
                        <span>Public - Visible to all users</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="w-3 h-3 text-blue-600" />
                        <span>Professional - Healthcare professionals only</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Lock className="w-3 h-3 text-red-600" />
                        <span>Private - Only visible to you</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-2">Data Sources:</p>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <span>🏛️</span>
                        <span>NPI Registry (official)</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>✓</span>
                        <span>Provider verified</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>👥</span>
                        <span>User contributed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>📈</span>
                        <span>Meeting insights</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Meeting History Summary */}
          <div className="mt-8 pt-8 border-t">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {selectedCustomer.source === 'NPI Registry' ? 'Provider Information' : 'Meeting History'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 mb-1">Total Meetings</p>
                <p className="text-2xl font-bold text-blue-900">{selectedCustomer.meetingsCompleted || 0}</p>
              </div>
              {selectedCustomer.lastMeeting ? (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 mb-1">Last Meeting</p>
                  <p className="text-2xl font-bold text-green-900">
                    {new Date(selectedCustomer.lastMeeting).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Last Meeting</p>
                  <p className="text-2xl font-bold text-gray-700">None</p>
                </div>
              )}
              {selectedCustomer.averageOrderValue ? (
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600 mb-1">Avg. Order Value</p>
                  <p className="text-2xl font-bold text-purple-900">{selectedCustomer.averageOrderValue}</p>
                </div>
              ) : selectedCustomer.source === 'NPI Registry' ? (
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600 mb-1">Data Source</p>
                  <p className="text-lg font-bold text-orange-900">NPI Registry</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Avg. Order Value</p>
                  <p className="text-2xl font-bold text-gray-700">-</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfileView;